/*
 * Copyright (c) 2025. Bolmate B.V.
 */
import './style/vatSettings.scss';

import React, {KeyboardEventHandler, MouseEventHandler, ReactElement, useContext} from 'react';
import BolProduct from "../../../models/bolProductModel";
import {AwesomeIcon, ChangeFunction, getSetting, Link, Loader} from '@dvrd/dvr-controls';
import {CountryVATValues, translateVatValue} from "../../../util/vatUtil";
import {BolmateButton, BolmateInput} from "../../controls";
import {AppContext} from "../../misc/appContext";
import useRight from "../../misc/useRight";
import {AccessKey, BolmateSelectItem} from "../../../util/interfaces";
import CountryFlag from "../../misc/countryFlag";
import CommonPagination from "../../misc/commonPagination";

interface Props {
    onClickSync: MouseEventHandler;
    onClickBulkUpload: MouseEventHandler;
    onClickProduct: (product: BolProduct) => MouseEventHandler;
    onClickDefault: MouseEventHandler;
    onChangeSearch: ChangeFunction<string>;
    onEnterSearch: KeyboardEventHandler;
    onChangePage: (nextPage: number) => MouseEventHandler;
    onChangeLimit: ChangeFunction<number>;
    products: Array<BolProduct>;
    total: number;
    search: string;
    defaultVATs: CountryVATValues;
    page: number;
    limit: number;
    isImporting: boolean;
    importingCount: number | null;
    loading: boolean;
}

const LIMIT_ITEMS: Array<BolmateSelectItem> = [
    {label: 10, value: 10},
    {label: 20, value: 20},
    {label: 50, value: 50},
]

export default function VATSettings(props: Props) {
    const {user} = useContext(AppContext).userContext;
    const [canSync, canBulkUpload] = useRight(AccessKey.PRODUCT_SETTINGS_EDIT_TITLE,
        AccessKey.PRODUCT_SETTINGS_EDIT_VAT, AccessKey.PRODUCT_SETTINGS_SYNC, AccessKey.PRODUCT_SETTINGS_UPLOAD);
    const {
        onClickSync, onClickBulkUpload, onClickProduct, onChangeSearch, onEnterSearch, onChangePage, onChangeLimit,
        products, search, defaultVATs, page, limit, isImporting, importingCount, loading, onClickDefault, total
    } = props;

    function renderTop() {
        const hideSyncForUsers: Array<string> = getSetting('hideProductSyncForUsers') ?? [];
        const hideSyncButton = (!!user && hideSyncForUsers.includes(user?.id)) || !canSync;
        return (
            <div className='top-row'>
                <div className='left'>
                    {!hideSyncButton && <BolmateButton onClick={onClickSync} label='Sync met Bol'
                                                       ornaments={{element: <AwesomeIcon name='rotate'/>}}
                                                       disabled={loading || isImporting} secondary/>}
                    <BolmateInput onChange={onChangeSearch} value={search} onEnter={onEnterSearch} label='Zoeken'
                                  placeholder='Zoek een product' disabled={loading} className='search-field' autoFocus/>
                </div>
                {renderDefaultBlock()}
            </div>
        )
    }

    function renderDefaultBlock() {
        return (
            <div className='default-block-col'>
                {renderImportLabel()}
                <div className='default-block'>
                    <div className='header-container'>
                        <label className='header-title'>Standaard BTW tarieven</label>
                        <AwesomeIcon name='cog' onClick={onClickDefault} className='default-vat-cog'/>
                    </div>
                    {renderCountryDefaults()}
                    {canBulkUpload &&
                        <Link onClick={onClickBulkUpload} className='upload-link'>
                            <AwesomeIcon name='file-excel' prefix='far'/> Upload bulk percentages
                        </Link>}
                </div>
            </div>
        )
    }

    function renderImportLabel() {
        if (!isImporting) return null;
        if (importingCount) return <label className='import-label'>Bezig met het ophalen
            van {importingCount} producten...</label>;
        return <label className='import-label'>Bezig met het ophalen van je producten...</label>;
    }

    function renderCountryDefaults() {
        const {sortedCountryCodes} = defaultVATs;
        return (
            <div className='country-defaults'>
                {sortedCountryCodes.map(renderCountryVAT(defaultVATs))}
            </div>
        )
    }

    function renderCountryVAT(values: CountryVATValues) {
        return function(countryCode: string, index: number) {
            return (
                <label key={index} className='country-vat' onClick={onClickDefault}>
                    <CountryFlag countryCode={countryCode}/>
                    <span className='vat-value'>{translateVatValue(values.getCountryValue(countryCode))}</span>
                </label>
            );
        }
    }

    function renderProducts() {
        return (
            <div className='products-container'>
                <Loader active={loading}/>
                <div className='row default-table-row head'>
                    <div/>
                    <label>EAN</label>
                    <label>NAAM</label>
                    <label>BTW TARIEF</label>
                    <div/>
                </div>
                {products.map(renderProduct)}
                <CommonPagination onChangePage={onChangePage} page={page} total={total} limit={limit}
                                  onChangeLimit={onChangeLimit} limitItems={LIMIT_ITEMS} limitItemsPosition='top'
                                  className='pagination'/>
            </div>
        )
    }

    function renderProduct(product: BolProduct, index: number) {
        return (
            <div key={index} className='row default-table-row' onClick={onClickProduct(product)}>
                {renderProductImage(product.imageURL)}
                <label>{product.ean}</label>
                {renderProductTitle(product)}
                {renderProductVAT(product)}
                <AwesomeIcon name='cog'/>
            </div>
        )
    }

    function renderProductImage(imageURL: string | null) {
        let image: ReactElement;
        if (imageURL) image = <img src={imageURL} className='product-image' alt=''/>;
        else image = <AwesomeIcon name='image' className='image-icon'/>;
        return (
            <div className='product-image-container'>
                {image}
            </div>
        )
    }

    function renderProductTitle(product: BolProduct) {
        return (
            <label className='product-title'>
                {product.isNew && <span className='new-tag'>Nieuw</span>}
                <span>{product.title}</span>
            </label>
        )
    }

    function renderProductVAT(product: BolProduct) {
        if (!product.vats.length) return <label>Standaard</label>;
        return (
            <div className='product-country-vats'>
                {product.vats.sortedCountryCodes.map(renderCountryVAT(product.vats))}
            </div>
        )
    }

    return (
        <div className='vat-settings'>
            <label className='settings-title'>Productinstellingen</label>
            {renderTop()}
            {renderProducts()}
        </div>
    )
}